import { UseQueryListReturn } from '@/utils'
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual'
import { useContext, createContext, useRef } from 'react'
import { QueryItem } from '@codeleap/query'

type VirtualListProps<T extends QueryItem, F> = {
  list: UseQueryListReturn<T, F>
  estimateSize?: (item: T) => number
}

type VirtualListContextValue<T extends QueryItem, F> = {
  virtualizer: Virtualizer<HTMLDivElement, Element>
  scrollRef: React.MutableRefObject<HTMLDivElement>
  loadNextPage: () => void
  list: UseQueryListReturn<T, F>
  virtualizerItemCount: number
  paginationIndicatorIndex: number
  lastItemIndex: number
}

const VirtualListContext = createContext({} as VirtualListContextValue<any, any>)

export function VirtualListProvider<T extends QueryItem, F>(props: React.PropsWithChildren<VirtualListProps<T, F>>) {
  const { list, estimateSize, children } = props

  const scrollRef = useRef<HTMLDivElement | null>(null)

  const listLength = list.data?.length ?? 0

  const virtualizerItemCount = list.hasNextPage ? listLength + 1 : listLength

  const paginationIndicatorIndex = list.hasNextPage ? virtualizerItemCount - 1 : null

  const lastItemIndex = list.hasNextPage ? virtualizerItemCount - 2 : virtualizerItemCount - 1

  const virtualizer = useVirtualizer({
    count: virtualizerItemCount,
    getScrollElement: () => scrollRef.current,
    estimateSize(index) {
      return estimateSize ? estimateSize(list.data[index]) : 100
    },
    overscan: 5
  })

  function loadNextPage() {
    const [lastItem] = [...virtualizer.getVirtualItems()].reverse()

    if (!lastItem) {
      return
    }

    if (lastItem.index >= list.data.length - 1 && list.hasNextPage && !list.isFetchingNextPage) {
      list.fetchNextPage()
    }
  }

  return (
    <VirtualListContext.Provider
      value={{
        virtualizer,
        loadNextPage,
        scrollRef,
        list,
        virtualizerItemCount,
        paginationIndicatorIndex,
        lastItemIndex
      }}
    >
      {children}
    </VirtualListContext.Provider>
  )
}

// This doesn't actully do anything in terms of rendering optimization, it's just here for when we eventually (maybe) run into performance issues
export function useVirtualListContext<T extends (val: VirtualListContextValue<any, any>) => any>(
  selector: T
): ReturnType<T> {
  return selector(useContext(VirtualListContext))
}
